/* ==========================================================================
   Explore the Coffee Belt — progressive enhancement
   Ported from the standalone demo (explore.srv891774.hstgr.cloud) 2026-06-13.

   The section server-renders: the masthead, an <svg> with the static land map
   inlined, an (empty) legend, and one <article data-region ...> per region
   containing the story, fly-through <video> and product shelf. Without JS the
   articles are the page (crawlable + AI-quotable + accessible).

   This script, when it runs:
     - marks the section .is-enhanced (CSS then hides the article stack and
       shows the map + legend),
     - draws the coffee-belt band, tropics, region dots and origin labels onto
       the inlined map and wires them,
     - builds the legend from the articles,
     - on region selection runs the fly-in (real fly-through film if the region
       has one, else the colour-flood + cloud-pass) and opens a full-screen
       panel whose scene still, format hotspots, format picker and product
       shelf are all sourced from that region's server-rendered article.
   ========================================================================== */
(function () {
  /* GA4 interaction tracking (2026-07-25): fires through the page's Google tag.
     Guarded — a missing gtag must never break the map. */
  function track(name, params) {
    try { if (typeof window.gtag === 'function') window.gtag('event', name, params || {}); } catch (err) { /* no-op */ }
  }

  'use strict';

  /* ---- static projection (must match scripts/build_map_svg.py) ---- */
  var W = 1600, LAT_TOP = 84, LAT_BOT = -56, H = W * (LAT_TOP - LAT_BOT) / 360; // 622.222…
  function px(lon, lat) { return [(lon + 180) / 360 * W, (LAT_TOP - lat) / (LAT_TOP - LAT_BOT) * H]; }

  /* ---- static config (not store data) ---- */
  var REGION_VIEW = {
    'south-america': [480, 330, 460, 290],
    'central-america': [330, 255, 330, 210],
    'africa': [820, 290, 330, 210],
    'asia': [1090, 300, 330, 210]
  };
  /* one pulsing clickable dot per region (AL's UX call) — on the region's heart */
  var REGION_DOTS = [
    { r: 'south-america', lon: -63.0, lat: -9.0 },
    { r: 'central-america', lon: -87.5, lat: 14.5 },
    { r: 'africa', lon: 38.5, lat: 6.5 },
    { r: 'asia', lon: 98.7, lat: 2.4 }
  ];
  /* country markers: informational only (AL's call — every pulsing dot
     promises its own experience; only regions deliver one) */
  var ORIGINS = [
    { c: 'Brazil', r: 'south-america', lon: -45.0, lat: -21.5, note: 'caramel · chocolate', lx: 40, ly: 60, anchor: 'start' },
    { c: 'Colombia', r: 'south-america', lon: -76.0, lat: 2.5, note: 'fruity · nutty', lx: -130, ly: 55, anchor: 'end' },
    { c: 'Peru', r: 'south-america', lon: -75.3, lat: -11.0, note: 'spicy · chocolate', lx: -95, ly: 80, anchor: 'end' },
    { c: 'Guatemala', r: 'central-america', lon: -90.7, lat: 14.6, note: 'smoky · floral', lx: -46, ly: -118, anchor: 'end' },
    { c: 'Costa Rica', r: 'central-america', lon: -84.1, lat: 9.9, note: 'citrus · nutty', lx: -150, ly: -32, anchor: 'end' },
    { c: 'Mexico', r: 'central-america', lon: -92.3, lat: 16.2, note: 'hazelnut · gentle', lx: -120, ly: -70, anchor: 'end' },
    { c: 'Honduras', r: 'central-america', lon: -88.8, lat: 14.8, note: 'crisp · nutty · spicy', lx: 60, ly: -86, anchor: 'start' },
    { c: 'El Salvador', r: 'central-america', lon: -89.2, lat: 13.7, note: 'vanilla · pear', lx: 36, ly: -44, anchor: 'start' },
    { c: 'Nicaragua', r: 'central-america', lon: -85.9, lat: 12.9, note: 'chocolate body', lx: 52, ly: -6, anchor: 'start' },
    { c: 'Ethiopia', r: 'africa', lon: 38.5, lat: 6.5, note: 'blueberry · flowers', lx: 54, ly: 42, anchor: 'start' },
    { c: 'Sumatra', r: 'asia', lon: 98.7, lat: 2.4, note: 'earthy · woody', lx: -30, ly: 88, anchor: 'end' }
  ];
  /* fixed hotspot coordinates over the menu still (carry across regions —
     the still is an edit of one approved frame, items never move) */
  var HOTSPOTS = [
    { fmt: 'beans', x: 36, y: 46, label: 'Beans — grind them your way' },
    { fmt: 'ground', x: 50, y: 49, label: 'Ground — cafetière & filter' },
    { fmt: 'pods', x: 69, y: 41, label: 'Perfect Pods — Nespresso compatible' },
    { fmt: 'bags', x: 85, y: 52, label: 'Coffee bags — brew in the mug' }
  ];
  // per-region hotspot coordinate overrides — when a still's layout differs from the others
  // (Africa hut was reshaped: smaller table, the mug sits lower/left of the shared position)
  var HOTSPOT_OVERRIDES = { africa: { bags: { x: 81, y: 58 } } };
  var FORMATS = [
    { id: 'all', label: 'Everything', icon: 'M4 12h16M4 6h16M4 18h16' },
    { id: 'beans', label: 'Beans · grinder', icon: 'M12 3c4 0 7 3.2 7 8s-3 10-7 10-7-5.2-7-10 3-8 7-8zM12 3c1.5 3 1.5 15 0 18' },
    { id: 'ground', label: 'Ground · cafetière', icon: 'M7 4h10v15a2 2 0 01-2 2H9a2 2 0 01-2-2zM7 4l10 0M12 8v0M17 8h2a1 1 0 011 1v4a1 1 0 01-1 1h-2M9 11h6' },
    { id: 'pods', label: 'Pods · pod machine', icon: 'M12 5a7 7 0 017 7H5a7 7 0 017-7zM5 12h14l-1.5 6h-11zM12 5V3' },
    { id: 'bags', label: 'Coffee bags · mug', icon: 'M5 8h11v7a3 3 0 01-3 3H8a3 3 0 01-3-3zM16 10h2a2 2 0 110 4h-2' }
  ];
  var REGION_TITLES = {
    'south-america': 'South America', 'central-america': 'Central America',
    'africa': 'Africa', 'asia': 'Asia'
  };
  var SVGNS = 'http://www.w3.org/2000/svg';
  var prefersReduced = function () { return matchMedia('(prefers-reduced-motion: reduce)').matches; };

  function init(section) {
    if (section.dataset.exploreReady) return;
    section.dataset.exploreReady = '1';

    var svg = section.querySelector('.explore-coffee__map');
    var legend = section.querySelector('.explore-coffee__legend');
    var articles = Array.prototype.slice.call(section.querySelectorAll('.explore-coffee__region'));
    if (!svg || !articles.length) return; // nothing to enhance

    /* per-region model, read from the server-rendered articles */
    var regions = {};
    articles.forEach(function (a) {
      var handle = a.getAttribute('data-region');
      if (!handle) return;
      var shelfFormats = {};
      a.querySelectorAll('.explore-coffee__shelf li').forEach(function (li) {
        var f = li.getAttribute('data-format');
        if (f) shelfFormats[f] = true;
      });
      var h2 = a.querySelector('h2');
      var flav = a.querySelector('.explore-coffee__flavours');
      var story = a.querySelector('.explore-coffee__story');
      var cap = a.querySelector('figcaption');
      regions[handle] = {
        handle: handle,
        article: a,
        title: (h2 ? h2.textContent : REGION_TITLES[handle] || handle).trim(),
        colour: a.getAttribute('data-colour') || '#a26b25',
        flavours: flav ? flav.textContent.trim() : '',
        storyHTML: story ? story.innerHTML : '',
        sceneCaption: cap ? cap.textContent.trim() : '',
        video: a.getAttribute('data-video') || '',
        videoPortrait: a.getAttribute('data-video-portrait') || '',
        still: a.getAttribute('data-still') || '',
        count: a.querySelectorAll('.explore-coffee__shelf li').length,
        formats: shelfFormats
      };
    });

    section.classList.add('is-enhanced');

    /* ---- overlays + panel live on <body> so the section's overflow/stacking
       never clips them ---- */
    var ui = buildUI();
    document.body.appendChild(ui.flood);
    document.body.appendChild(ui.clouds);
    document.body.appendChild(ui.flyvideo);
    document.body.appendChild(ui.panel);

    drawMap(svg, regions);
    drawLegend(legend, regions);

    var zooming = false;

    function flyTo(handle) {
      var R = regions[handle];
      track('explore_region_select', { explore_region: handle });
      if (!R || zooming) return;
      zooming = true;
      if (prefersReduced()) { openPanel(handle); zooming = false; return; }
      if (R.video) { flyVideo(handle, R); return; }

      /* 1 — zoom the viewBox toward the region */
      animateView(svg, REGION_VIEW[handle], 1150);

      /* 2 — colour flood washes the screen in the region's hue */
      ui.flood.style.background = 'radial-gradient(120% 120% at 50% 50%, ' + R.colour + ' 0%, ' + R.colour + 'cc 45%, #3a2c1a 100%)';
      setTimeout(function () { ui.flood.style.opacity = '.96'; }, 420);

      /* 3 — cloud pass hides the seam, panel opens behind it */
      ui.clouds.innerHTML = cloudSpans();
      setTimeout(function () { ui.clouds.classList.add('run'); }, 680);
      setTimeout(function () { openPanel(handle); ui.flood.style.opacity = '0'; }, 1350);
      setTimeout(function () {
        ui.clouds.classList.remove('run'); ui.clouds.innerHTML = '';
        resetView(svg); zooming = false;
      }, 2300);
    }

    /* real fly-through: the clip ends on the hut still, which is also the
       panel's scene image — so the moment the overlay fades, the panel shows
       the exact frame the film stopped on. That continuity is the seam test. */
    function flyVideo(handle, R) {
      var wrap = ui.flyvideo, vid = wrap.querySelector('video');
      var portrait = matchMedia('(orientation: portrait)').matches && R.videoPortrait;
      vid.src = portrait ? R.videoPortrait : R.video;
      animateView(svg, REGION_VIEW[handle], 1000);
      var done = false;
      function finish() {
        if (done) return; done = true;
        openPanel(handle);
        wrap.classList.remove('show');
        setTimeout(function () {
          vid.pause(); vid.removeAttribute('src'); vid.load(); resetView(svg);
        }, 500);
        zooming = false;
      }
      setTimeout(function () { wrap.classList.add('show'); var p = vid.play(); if (p && p.catch) p.catch(finish); }, 850);
      vid.onended = finish;
      wrap.onclick = finish;
    }

    /* ---------- panel ---------- */
    function openPanel(handle) {
      var R = regions[handle];
      ui.title.textContent = R.title;
      ui.sub.innerHTML = R.storyHTML;
      ui.crumb.textContent = 'the coffee belt → ' + R.title;

      var frame = ui.frame;
      frame.style.setProperty('--c', R.colour);
      // clear old hotspots
      Array.prototype.slice.call(frame.querySelectorAll('.explore-coffee__hotspot')).forEach(function (h) { h.remove(); });
      if (R.still) {
        frame.style.backgroundImage = "url('" + R.still + "')";
        HOTSPOTS.forEach(function (h) {
          var b = document.createElement('button'); // always show all 4 — the scene depicts all four formats
          b.className = 'explore-coffee__hotspot';
          b.dataset.fmt = h.fmt;
          var ov = (HOTSPOT_OVERRIDES[handle] || {})[h.fmt] || h;
          b.style.left = ov.x + '%'; b.style.top = ov.y + '%';
          b.setAttribute('aria-label', h.label);
          b.setAttribute('aria-pressed', 'false');
          b.innerHTML = '<span class="ring"></span><span class="ring r2"></span><span class="core"></span><span class="tip">' + h.label + '</span>';
          b.addEventListener('click', function () { track('explore_hotspot_click', { explore_region: handle, explore_format: h.fmt }); setFormat(handle, h.fmt); });
          frame.appendChild(b);
        });
        ui.sceneCaption.textContent = R.sceneCaption || '';
      } else {
        frame.style.backgroundImage = '';
        ui.sceneCaption.textContent = R.sceneCaption || '';
      }

      buildFormats(handle, R);
      buildShelf(R);
      setFormat(handle, 'all');

      ui.panel.classList.add('open');
      document.body.classList.add('explore-coffee-locked');
      ui.back.focus();
    }

    function buildFormats(handle, R) {
      ui.formats.innerHTML = '';
      FORMATS.forEach(function (f) {
        var b = document.createElement('button');
        b.className = 'explore-coffee__fmt';
        b.dataset.fmt = f.id;
        b.setAttribute('aria-pressed', String(f.id === 'all'));
        b.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="' + f.icon + '"/></svg>' + f.label;
        b.addEventListener('click', function () { track('explore_format_click', { explore_region: handle, explore_format: f.id }); setFormat(handle, f.id); });
        ui.formats.appendChild(b);
      });
    }

    /* clone the region article's shelf items into the panel (preserves
       data-format so the picker/hotspots can filter without a re-fetch) */
    function buildShelf(R) {
      ui.shelf.innerHTML = '';
      R.article.querySelectorAll('.explore-coffee__shelf > li').forEach(function (li) {
        ui.shelf.appendChild(li.cloneNode(true));
      });
    }

    function setFormat(handle, fmt) {
      Array.prototype.slice.call(ui.formats.querySelectorAll('.explore-coffee__fmt')).forEach(function (x) {
        x.setAttribute('aria-pressed', String(x.dataset.fmt === fmt));
      });
      Array.prototype.slice.call(ui.frame.querySelectorAll('.explore-coffee__hotspot')).forEach(function (h) {
        h.setAttribute('aria-pressed', String(h.dataset.fmt === fmt));
      });
      Array.prototype.slice.call(ui.shelf.children).forEach(function (li) {
        var f = li.getAttribute('data-format');
        li.hidden = !(fmt === 'all' || f === fmt);
      });
    }

    function closePanel() {
      ui.panel.classList.remove('open');
      document.body.classList.remove('explore-coffee-locked');
    }

    ui.shelf.addEventListener('click', function (e) {
      var a = e.target && e.target.closest ? e.target.closest('a') : null;
      if (a) track('explore_product_click', { explore_product: a.getAttribute('href') || '' });
    });
    ui.back.addEventListener('click', closePanel);
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && ui.panel.classList.contains('open')) closePanel(); });

    /* ---------- map drawing ---------- */
    function drawMap(svg, regions) {
      var beltTop = px(0, 23.43)[1], beltBot = px(0, -23.43)[1], eq = px(0, 0)[1];
      // belt band sits BEHIND the land paths
      svg.insertAdjacentHTML('afterbegin',
        '<rect class="belt-band" x="0" y="' + beltTop + '" width="' + W + '" height="' + (beltBot - beltTop) + '"/>');
      // tropics + equator + labels on top of the land
      var top = '';
      top += '<line class="tropic" x1="0" x2="' + W + '" y1="' + beltTop + '" y2="' + beltTop + '"/>';
      top += '<line class="tropic" x1="0" x2="' + W + '" y1="' + beltBot + '" y2="' + beltBot + '"/>';
      top += '<line class="tropic" x1="0" x2="' + W + '" y1="' + eq + '" y2="' + eq + '" opacity=".5"/>';
      top += '<text class="tropic-label" x="14" y="' + (beltTop - 7) + '">Tropic of Cancer</text>';
      top += '<text class="tropic-label" x="14" y="' + (beltBot + 16) + '">Tropic of Capricorn</text>';
      top += '<text class="tropic-label" x="' + (W - 150) + '" y="' + (eq - 7) + '">Equator</text>';
      // origin info labels (non-interactive)
      ORIGINS.forEach(function (o) {
        var p = px(o.lon, o.lat), x = p[0], y = p[1], tx = x + o.lx, ty = y + o.ly;
        var elbow = 'M' + x + ' ' + y + 'L' + tx + ' ' + ty + 'l' + (o.anchor === 'end' ? -12 : 12) + ' 0';
        var labelX = tx + (o.anchor === 'end' ? -16 : 16);
        top += '<g class="info-dot" aria-hidden="true">'
          + '<path class="leader" d="' + elbow + '"/>'
          + '<circle class="core" cx="' + x + '" cy="' + y + '" r="3"/>'
          + '<text class="olabel" x="' + labelX + '" y="' + (ty + 4) + '" text-anchor="' + o.anchor + '">' + o.c + '</text>'
          + '<text class="onote" x="' + labelX + '" y="' + (ty + 18) + '" text-anchor="' + o.anchor + '">' + o.note + '</text>'
          + '</g>';
      });
      // one pulsing clickable dot per region
      REGION_DOTS.forEach(function (rd) {
        if (!regions[rd.r]) return;
        var p = px(rd.lon, rd.lat), x = p[0], y = p[1];
        top += '<g class="dot" data-region="' + rd.r + '" role="button" tabindex="0" aria-label="Explore ' + (REGION_TITLES[rd.r] || rd.r) + '">'
          + '<circle class="halo" cx="' + x + '" cy="' + y + '" r="9"/>'
          + '<circle class="halo" cx="' + x + '" cy="' + y + '" r="9" style="animation-delay:1.3s"/>'
          + '<circle class="core" cx="' + x + '" cy="' + y + '" r="5.5"/>'
          + '<circle cx="' + x + '" cy="' + y + '" r="20" fill="transparent"/>'
          + '</g>';
      });
      svg.insertAdjacentHTML('beforeend', top);

      // wire region dots
      Array.prototype.slice.call(svg.querySelectorAll('.dot')).forEach(function (el) {
        var go = function () { flyTo(el.getAttribute('data-region')); };
        el.addEventListener('click', go);
        el.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go(); } });
      });
      // wire the coloured country shapes to their region (region read from class)
      Array.prototype.slice.call(svg.querySelectorAll('.origin')).forEach(function (el) {
        var handle = ['south-america', 'central-america', 'africa', 'asia'].filter(function (r) { return el.classList.contains(r); })[0];
        if (!handle || !regions[handle]) return;
        el.style.cursor = 'pointer';
        el.setAttribute('role', 'button');
        el.setAttribute('tabindex', '0');
        el.setAttribute('aria-label', 'Explore ' + regions[handle].title);
        var go = function () { flyTo(handle); };
        el.addEventListener('click', go);
        el.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go(); } });
      });
    }

    function drawLegend(legend, regions) {
      if (!legend) return;
      legend.innerHTML = '';
      articles.forEach(function (a) {
        var handle = a.getAttribute('data-region');
        var R = regions[handle];
        if (!R) return;
        var b = document.createElement('button');
        b.className = 'explore-coffee__region-btn';
        b.style.setProperty('--c', R.colour);
        b.innerHTML = '<span class="rname">' + R.title + '</span>'
          + '<span class="rflav">' + R.flavours + '</span>'
          + '<span class="rcount">' + R.count + ' coffee' + (R.count === 1 ? '' : 's') + '</span>';
        b.addEventListener('click', function () { flyTo(handle); });
        legend.appendChild(b);
      });
    }
  }

  /* ---- view animation helpers ---- */
  function animateView(svg, to, dur) {
    var from = [0, 0, W, H], t0 = performance.now();
    var ease = function (t) { return 1 - Math.pow(1 - t, 3.2); };
    (function step(now) {
      var k = ease(Math.min(1, (now - t0) / dur));
      svg.setAttribute('viewBox', from.map(function (v, i) { return v + (to[i] - v) * k; }).join(' '));
      if (k < 1) requestAnimationFrame(step);
    })(t0);
  }
  function resetView(svg) { svg.setAttribute('viewBox', '0 0 ' + W + ' ' + H); }
  function cloudSpans() {
    var out = '';
    for (var i = 0; i < 7; i++) {
      var top = Math.sin(i * 2.7) * 38 + 28, h = 140 + (i % 3) * 90;
      out += '<span style="top:' + top + '%;left:' + ((i * 13) - 20) + '%;width:' + (h * 2.2) + 'px;height:' + h + 'px;animation-delay:' + (i * 55) + 'ms"></span>';
    }
    return out;
  }

  /* ---- build the body-level overlay + panel DOM once per section ---- */
  function buildUI() {
    var flood = el('div', 'explore-coffee__flood');
    var clouds = el('div', 'explore-coffee__clouds'); clouds.setAttribute('aria-hidden', 'true');

    var flyvideo = el('div', 'explore-coffee__flyvideo'); flyvideo.setAttribute('aria-hidden', 'true');
    var vid = document.createElement('video'); vid.muted = true; vid.setAttribute('playsinline', ''); vid.setAttribute('preload', 'auto');
    var skip = el('span', 'explore-coffee__skip'); skip.textContent = 'tap to skip';
    flyvideo.appendChild(vid); flyvideo.appendChild(skip);

    var panel = el('section', 'explore-coffee__panel');
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    panel.setAttribute('aria-labelledby', 'explore-coffee-panel-title');

    var head = el('div', 'explore-coffee__panel-head');
    var back = document.createElement('button'); back.className = 'explore-coffee__back'; back.type = 'button'; back.innerHTML = '← Back to the belt';
    var crumb = el('span', 'explore-coffee__crumb');
    head.appendChild(back); head.appendChild(crumb);

    var body = el('div', 'explore-coffee__panel-body');
    var fig = el('figure', 'explore-coffee__scene-fig');
    var frame = el('div', 'explore-coffee__frame');
    var sceneCaption = document.createElement('figcaption');
    fig.appendChild(frame); fig.appendChild(sceneCaption);

    var col = document.createElement('div');
    var title = el('h2', 'explore-coffee__panel-title'); title.id = 'explore-coffee-panel-title';
    var sub = el('div', 'explore-coffee__panel-sub');
    var formats = el('div', 'explore-coffee__formats'); formats.setAttribute('role', 'group'); formats.setAttribute('aria-label', 'Choose your format');
    var shelf = el('ul', 'explore-coffee__panel-shelf');
    col.appendChild(title); col.appendChild(sub); col.appendChild(formats); col.appendChild(shelf);

    body.appendChild(fig); body.appendChild(col);
    panel.appendChild(head); panel.appendChild(body);

    return { flood: flood, clouds: clouds, flyvideo: flyvideo, panel: panel, back: back, crumb: crumb, frame: frame, sceneCaption: sceneCaption, title: title, sub: sub, formats: formats, shelf: shelf };
  }
  function el(tag, cls) { var n = document.createElement(tag); n.className = cls; return n; }

  function boot() {
    Array.prototype.slice.call(document.querySelectorAll('.explore-coffee')).forEach(init);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // Shopify theme editor: re-init if the section is re-rendered
  document.addEventListener('shopify:section:load', function (e) {
    var s = e.target && e.target.querySelector ? e.target.querySelector('.explore-coffee') : null;
    if (s) { s.removeAttribute('data-explore-ready'); init(s); }
  });
})();
